'use client';

import { useCryptoDetail } from '@/hooks/use-crypto-detail';
import PythraSummary from '../ui/PythraSummary';
import CryptoHeader from '../crypto-detail/CryptoHeader';
import CryptoCapitalOverview from '../crypto-detail/CryptoCapitalOverview';
import CryptoPriceChart from '../crypto-detail/CryptoPriceChart';
import FixedChatWindow from '../message/FixedChatWindow';
import { useEffect, useMemo } from 'react';
import { useRightDrawer } from '@/contexts/DrawerContext';
import CryptoReleatedMarket from '../crypto-detail/CryptoReleatedMarket';
import CryptoFinancial from '../crypto-detail/CryptoFinancial';
import CryptoValuation from '../crypto-detail/CryptoValuation';
import CryptoReports from '../crypto-detail/CryptoReports';
import { useCryptoRelatedNews } from '@/hooks/use-crypto-related-news';
import { useTranslation } from '@/lib/i18n';
import NewsTimeline, { createNewsTimelineGroups } from '../ui/NewsTimeline';
import { useHeader } from '@/contexts/HeaderContext';
import { Skeleton } from '@mui/material';

export default function CryptoPage({
  symbol,
  openInSidebar,
}: {
  symbol: string;
  openInSidebar?: boolean;
}) {
  const { t } = useTranslation();
  const { setShowBackButton, setIsTransparent } = useHeader();
  const { data, isLoading } = useCryptoDetail(symbol, {
    datas: ['question', 'summary', 'detail'],
  });
  const { data: newsData } = useCryptoRelatedNews(symbol);
  const newsTimelineGroups = useMemo(
    () => createNewsTimelineGroups(newsData?.news || []),
    [newsData?.news],
  );
  const { openDrawer } = useRightDrawer();

  useEffect(() => {
    if (newsTimelineGroups.length > 0 && !openInSidebar) {
      openDrawer(<NewsTimeline groups={newsTimelineGroups} />, t('Live Updates'), true);
    }
  }, [newsTimelineGroups, openDrawer, t, openInSidebar]);

  useEffect(() => {
    setShowBackButton(true);
    setIsTransparent(false);
    return () => setShowBackButton(false);
  }, [setShowBackButton, setIsTransparent]);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-245 flex flex-col items-center gap-3 mb-6">
        <Skeleton variant="rounded" width="100%" height={40} sx={{ bgcolor: 'grey.800' }} />
        <Skeleton variant="rounded" width="100%" height={280} sx={{ bgcolor: 'grey.800' }} />
      </div>
    );
  }

  return (
    <>
      <div className="mx-auto max-w-245 space-y-4 pb-25">
        <CryptoHeader data={data} />
        <CryptoCapitalOverview data={data} />
        <CryptoPriceChart
          symbol={symbol}
          supportsCone={data?.supports_cone}
          openInSidebar={openInSidebar}
        />
        <PythraSummary summary={data?.summary} />
        <CryptoReleatedMarket symbol={symbol} />
        <CryptoFinancial symbol={symbol} openInSidebar={openInSidebar} />
        <CryptoValuation symbol={symbol} openInSidebar={openInSidebar} />
        {/* <CryptoReports symbol={symbol} /> */}
      </div>

      {!isLoading && !openInSidebar && (
        <FixedChatWindow
          queryType="crypto"
          suggestions={data?.recommended_questions || []}
          sourceId={`crypto_${data?.symbol}`}
          userContext={
            data
              ? JSON.stringify({
                  context_type: 'crypto',
                  data: { ...data, trending_card: data },
                })
              : undefined
          }
        />
      )}
    </>
  );
}
